import { CookiesProvider } from 'react-cookie';
import { HelmetProvider } from 'react-helmet-async';
import { RouterProvider } from 'react-router-dom';

import * as Sentry from '@sentry/react';
import { theme } from '@looko-design-system';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { ConfigProvider } from 'antd';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isToday from 'dayjs/plugin/isToday';
import isYesterday from 'dayjs/plugin/isYesterday';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import minMax from 'dayjs/plugin/minMax';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import 'driver.js/dist/driver.css';
import { Provider as JotaiProvider } from 'jotai';
import { SnackbarProvider } from 'notistack';
import { RecoilRoot } from 'recoil';

import { aclosetDayjsPlugin, enableDayjsPlugin } from '@looko-corp/acloset-react-kit/src/plugins/dayjs-plugin';
import { enableNumberPlugin } from '@looko-corp/acloset-react-kit/src/plugins/number-plugin';

import { AclosetServiceErrorSnackbar } from '@/components/notification/AclosetServiceErrorSnackbar';

import { AppInitializeProvider } from './app/AppInitializeProvider';
import { AppLevelQueryClientProvider } from './app/AppLevelQueryClientProvider';
import { DevtoolsProvider } from './app/devtools/devtool-storage';
import { AppRouter } from './router';
import './styles/aimsJspreadSheet.css';
import './styles/driver-js.css';
import './styles/global.css';
import './styles/mui-datagrid.css';
import './styles/resetAntd.css';

dayjs.locale('ko');
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault('Asia/Seoul');
dayjs.extend(aclosetDayjsPlugin);
dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);
dayjs.extend(isToday);
dayjs.extend(isYesterday);
dayjs.extend(minMax);
dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

enableDayjsPlugin();
enableNumberPlugin();

/**
 * 앱 루트.
 */
function App() {
  return (
    <CookiesProvider>
      <JotaiProvider>
        <DevtoolsProvider>
          <RecoilRoot>
            <HelmetProvider>
              <ThemeProvider theme={theme}>
                {/* TODO: antd용, mui로 다 옮기면 삭제 */}
                <ConfigProvider
                  theme={{
                    token: {
                      fontFamily: `'Pretendard Variable', Pretendard, -apple-system, BlinkMacSystemFont, system-ui, Roboto,
            'Helvetica Neue', 'Segoe UI', 'Apple SD Gothic Neo', 'Noto Sans KR', 'Malgun Gothic', 'Apple Color Emoji',
            'Segoe UI Emoji', 'Segoe UI Symbol', sans-serif`,
                      fontSize: 16,
                    },
                  }}
                >
                  <CssBaseline />
                  <SnackbarProvider
                    maxSnack={3}
                    autoHideDuration={2000}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    Components={{
                      'acloset-service-error': AclosetServiceErrorSnackbar,
                    }}
                  >
                    <AppLevelQueryClientProvider>
                      <AppInitializeProvider>
                        <RouterProvider router={AppRouter} />
                      </AppInitializeProvider>
                    </AppLevelQueryClientProvider>
                  </SnackbarProvider>
                </ConfigProvider>
              </ThemeProvider>
            </HelmetProvider>
          </RecoilRoot>
        </DevtoolsProvider>
      </JotaiProvider>
    </CookiesProvider>
  );
}

export default Sentry.withProfiler(App);
